/**
 * useMediaWorkflow Hook
 *
 * Manages the two-phase media generation workflow state:
 * - Tracks when media generation is in progress
 * - Handles workflow state transitions
 * - Provides callbacks for media selection and continuation
 */

import { useState, useCallback, useEffect } from 'react';
import { apiClient } from '@/services/api';

export interface GeneratedMedia {
  url: string;
  asset_id?: string;
  media_type: 'image' | 'video';
  job_id: string;
  filename: string;
  selected: boolean;
}

export interface MediaWorkflowState {
  state: 'none' | 'media_generation_pending' | 'media_selection_pending' | 'media_selected' | 'completed';
  session_id?: string;
  job_ids: string[];
  generated_media: GeneratedMedia[];
  selected_media: GeneratedMedia[];
  all_jobs_complete: boolean;
}

interface UseMediaWorkflowOptions {
  conversationId?: string;
  onWorkflowComplete?: (selectedMedia: GeneratedMedia[]) => void;
  onError?: (error: string) => void;
}

export const useMediaWorkflow = (options: UseMediaWorkflowOptions) => {
  const { conversationId, onWorkflowComplete, onError } = options;

  const [isCanvasOpen, setIsCanvasOpen] = useState(false);
  const [workflowState, setWorkflowState] = useState<MediaWorkflowState | null>(null);
  const [loading, setLoading] = useState(false);

  // Check for active workflow on mount/conversationId change
  useEffect(() => {
    if (conversationId) {
      checkWorkflowStatus();
    }
  }, [conversationId]);

  const checkWorkflowStatus = useCallback(async (forceOpen?: boolean) => {
    if (!conversationId) return;

    try {
      setLoading(true);
      const response = await apiClient.get(
        `/api/v1/campaigns/${conversationId}/media-workflow`
      );
      const state = response.data;
      setWorkflowState(state);

      // Auto-open canvas only if:
      // 1. Workflow just started (forceOpen=true from detectMediaGeneration)
      // 2. Workflow is in selection pending state (user needs to select media)
      if (forceOpen && state.state === 'media_generation_pending') {
        setIsCanvasOpen(true);
      } else if (state.state === 'media_selection_pending') {
        // Always open for selection (user action required)
        setIsCanvasOpen(true);
      }
      // For existing completed workflows, don't auto-open on reload
    } catch (err) {
      console.error('Failed to check workflow status:', err);
    } finally {
      setLoading(false);
    }
  }, [conversationId]);

  const startMediaGeneration = useCallback(() => {
    setIsCanvasOpen(true);
    checkWorkflowStatus();
  }, [checkWorkflowStatus]);

  const handleClose = useCallback(() => {
    setIsCanvasOpen(false);
  }, []);

  const handleContinue = useCallback(
    async (selectedMedia: GeneratedMedia[]) => {
      if (!conversationId) return;

      try {
        setLoading(true);

        // Call continue endpoint to trigger Phase 2
        const response = await apiClient.post(
          `/api/v1/campaigns/${conversationId}/media-workflow/continue`
        );

        setIsCanvasOpen(false);
        setWorkflowState(null);

        if (onWorkflowComplete) {
          onWorkflowComplete(selectedMedia);
        }

        return response.data;
      } catch (err: any) {
        console.error('Failed to continue workflow:', err);
        if (onError) {
          onError(err.message || 'Failed to continue with selected media');
        }
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [conversationId, onWorkflowComplete, onError]
  );

  const handleError = useCallback(
    (error: string) => {
      if (onError) {
        onError(error);
      }
    },
    [onError]
  );

  // Detect if LLM response contains media generation tool calls
  const detectMediaGeneration = useCallback(
    (toolResults?: Array<{ tool: string; success: boolean; job_id?: string }>) => {
      if (!toolResults) return false;

      const hasMediaGeneration = toolResults.some(
        (result) =>
          result.tool === 'media_generation_service' && result.success
      );

      if (hasMediaGeneration) {
        // Open canvas to show generation progress (forceOpen=true for new workflows)
        setIsCanvasOpen(true);
        checkWorkflowStatus(true);
        return true;
      }

      return false;
    },
    [checkWorkflowStatus]
  );

  // Toggle canvas manually (for collapsible button)
  const toggleCanvas = useCallback(() => {
    setIsCanvasOpen(prev => !prev);
  }, []);

  // Clear workflow state (used when transitioning to campaign generation)
  const clearWorkflowState = useCallback(() => {
    setWorkflowState(null);
    setIsCanvasOpen(false);
  }, []);

  return {
    isCanvasOpen,
    workflowState,
    loading,
    startMediaGeneration,
    handleClose,
    handleContinue,
    handleError,
    detectMediaGeneration,
    checkWorkflowStatus,
    toggleCanvas,
    clearWorkflowState,
  };
};

export default useMediaWorkflow;
